import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import { activityKeys, addActivity, ANY_TIME_OF_DAY } from "@/api/Activities";
import type { INewActivity } from "@/api/Activities";
import type { ICompetencePickerFields } from "./CompetencePicker";
import { CompetencePicker } from "./CompetencePicker";
import Checkbox from "@/components/Checkbox/Checkbox";
import { deducedError } from "@/Utils/ErrorUtils";
import { FilledButton } from "@medoma/components/Button/Button";
import type {
  IVideoType,
  IMeasurementsType,
  IActivityCategory,
} from "@medoma/models/activities";
import {
  measurementSchema,
  categorySchema,
  timeOfDaySchema,
  getActivityCategoryConfig,
  orderedCategories,
} from "@medoma/models/activities";
import { buildNewActivity } from "./buildNewActivity";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import type { ITitleInputFields } from "./TitleInput";
import { TitleInput } from "./TitleInput";
import {
  DescriptionInput,
  type IDescriptionInputFields,
} from "./DescriptionInput";
import type { ISchedulingFields } from "./Scheduling/Scheduling";
import { Scheduling } from "./Scheduling/Scheduling";
import {
  activityFrequencySchema,
  customRecurrenceIntervalUnitSchema,
  recurringOptionSchema,
} from "./Scheduling/schedulingModels";
import { useEffect } from "react";
import { MeasurementsPicker } from "@/forms/AddActivityForm/MeasurementsPicker";
import { getTimeFields, getTimespan } from "./activityTimeUtils";
import { type IInvoicingCodesPickerFields } from "@/forms/AddActivityForm/InvoicingPicker";
import { AddInvoicingCodesAction } from "@/forms/AddActivityForm/AddInvoicingCodesAction";
import { saveActivityInvoicingCodes, shouldSaveInvoice } from "@/api/Invoicing";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import {
  ActivityCategoryPicker,
  type IActivityCategoryPickerFields,
} from "@/forms/AddActivityForm/ActivityCategoryPicker";
import type { IPatientId } from "@medoma/models/patients";
import { useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { useNewActivityOutletContext } from "@/pages/commandcenter/NewActivity/newActivityUtils";
import {
  calendarDateSchema,
  dateInputSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import {
  ActivityDuration,
  type IActivityDurationFields,
} from "@/forms/AddActivityForm/ActivityDuration";
import { DEFAULT_ACTIVITY_DURATION_OPTION } from "@/forms/AddActivityForm/activityDurationModels";
import { convertDurationToMinutes } from "@/forms/AddActivityForm/activityDurationUtils";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";
import { routeKeys } from "@/api/Routes";
import { shiftKeys } from "@/api/Shifts";

export type IAddActivityFormData = ITitleInputFields &
  ISchedulingFields &
  ICompetencePickerFields &
  IInvoicingCodesPickerFields &
  IActivityCategoryPickerFields &
  IActivityDurationFields &
  IDescriptionInputFields & {
    doubleStaffing: boolean;
    hidden: boolean;
    type: IVideoType;
    measurements: IMeasurementsType[];
  };

const isValidPatientId = (
  patientId: string | undefined | null,
): patientId is string => {
  return patientId !== undefined && patientId !== null;
};

export const AddActivityForm = ({
  patientId,
}: {
  patientId: IPatientId | undefined;
}) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { defaultStartDate } = useNewActivityOutletContext();

  const methods = useForm<IAddActivityFormData>({
    defaultValues: {
      hidden: false,
      recurrencesPerDay: "1",
      customRecurrenceIntervalUnit:
        customRecurrenceIntervalUnitSchema.enum.weeks,
      durationOption: DEFAULT_ACTIVITY_DURATION_OPTION,
      startDate: defaultStartDate
        ? machineFormat(defaultStartDate, "yyyy-MM-dd")
        : undefined,
      // When there is no patient, AdminTask is the only available category, so preselect it.
      category: patientId ? undefined : categorySchema.enum.AdminTask,
      timeCategory: timeOfDaySchema.enum.Specific,
      measurements: [
        measurementSchema.enum.bloodPressure,
        measurementSchema.enum.pulse,
        measurementSchema.enum.saturation,
        measurementSchema.enum.temperature,
        measurementSchema.enum.weight,
      ],
      frequency: activityFrequencySchema.enum.oneTime,
      procedureCodes: [], // Must be an array, BE doesn't support undefined.
      productCodes: [], // Must be an array, BE doesn't support undefined.
    },
  });

  const {
    formState: { errors, isDirty },
    register,
    reset,
    resetField,
    getValues,
    handleSubmit,
    setError,
    watch,
  } = methods;

  const { organization } = useUserProfile();

  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const queryClient = useQueryClient();
  const { mutate, isPending: isAdding } = useMutation({
    mutationFn: ({
      newActivity,
      procedureCodes,
      productCodes,
    }: {
      newActivity: INewActivity;
      procedureCodes: string[];
      productCodes: string[];
    }) =>
      addActivity(newActivity).then(() =>
        isValidPatientId(newActivity.patientId) &&
        shouldSaveInvoice({
          invoicingActivated: organizationUsesInvoicingCodes(organization),
          category: newActivity.category,
          procedureCodes,
          productCodes,
          activityHasPatient: isValidPatientId(newActivity.patientId),
        })
          ? saveActivityInvoicingCodes({
              activityId: newActivity.activityId,
              patientId: newActivity.patientId,
              procedureCodes,
              productCodes,
            })
          : null,
      ),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: activityKeys.all }),
        queryClient.invalidateQueries({ queryKey: routeKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: shiftKeys.listWithContents({}),
        }),
      ]);
      void navigate("../..");
    },
  });

  const validateAndSubmit = handleSubmit((validatedFormData) => {
    const {
      category,
      description,
      doubleStaffing,
      frequency,
      recurrence,
      requiredCompetences,
      customRecurrenceInterval,
      customRecurrenceIntervalUnit,
      startDate,
      endDate,
      timeCategory,
      timeSensitivity,
      timeslots,
      title,
      weekdays,
      hidden,
      measurements,
      durationOption,
      customDuration,
      customDurationUnit,
      procedureCodes,
      productCodes,
    } = validatedFormData;

    // Convert duration fields to minutes
    const duration = convertDurationToMinutes(
      durationOption,
      customDuration,
      customDurationUnit,
    );

    const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;
    const isRecurring =
      frequency === activityFrequencySchema.enum.recurring &&
      recurrence !== null;
    const isRecurringAsInterval =
      recurrence !== recurringOptionSchema.enum.onSelectedWeekdays;

    const recurringTimeOfDayFields = isAnyTimeOfDay
      ? {
          isAnyTimeOfDay: true as const,
          timeslots: undefined,
        }
      : {
          isAnyTimeOfDay: false as const,
          timeslots: timeslots as {
            time: string;
          }[],
        };

    const timeFields = isRecurring
      ? {
          ...getTimeFields({
            isRecurringAsInterval,
            weekdays,
            recurrence,
            customRecurrenceInterval,
            customRecurrenceIntervalUnit,
            ...recurringTimeOfDayFields,
          }),
          endDate: dateInputSchema.decode(endDate) ?? undefined,
        }
      : {
          time: isAnyTimeOfDay
            ? ANY_TIME_OF_DAY
            : // @ts-expect-error we know that there is exactly one timeslot, but TS doesn't.
              timeslots[0].time,
        }; // We only have one timeslot if it's not recurring.

    if (
      category !== categorySchema.enum.AdminTask &&
      !isValidPatientId(patientId)
    ) {
      displayErrorMessageAlert(
        t`Ett oväntat fel har inträffat. Kontakta support.`,
      );
      return;
    }

    const newActivity = buildNewActivity({
      category,
      title,
      description,
      duration,
      hidden,
      startDate: calendarDateSchema.decode(startDate),
      timespan: getTimespan({ isAnyTimeOfDay, timeSensitivity }),
      patientId: patientId ?? null,
      requiredCompetences,
      doubleStaffing,
      measurements,
      timeFields,
    });

    mutate({ newActivity, procedureCodes, productCodes });
  });

  // Manual casting required due to category field is undefined when no category is selected.
  // We don't want the IActivityCategoryPicker field to be typed as undefined, since we know it will be set when a category is selected.

  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const currentCategory = watch(`category`) as IActivityCategory | undefined;
  const activityHasPatient = patientId !== undefined;

  const {
    allowsHidden: activityIsHideable,
    allowsCompetence: currentCategoryAllowsCompetence,
    requiresCompetence: currentCategoryRequiresCompetence,
    allowsInvoicingCode: activityAllowsInvoicingCode,
    allowsDuration: currentCategoryAllowsDuration,
    allowsDoubleStaffing: currentCategoryMayRequireDoubleStaffing,
  } = currentCategory
    ? getActivityCategoryConfig({
        category: currentCategory,
        activityHasPatient,
      })
    : {
        allowsHidden: false,
        allowsCompetence: false,
        requiresCompetence: false,
        allowsInvoicingCode: false,
        allowsDuration: false,
        allowsDoubleStaffing: false,
      };

  const activityMayHaveInvoicingCodes =
    activityAllowsInvoicingCode && organizationUsesInvoicingCodes(organization);

  const availableCategories = orderedCategories.filter((category) => {
    if (!activityHasPatient) {
      return category === categorySchema.enum.AdminTask;
    }
    return true;
  });

  useEffect(() => {
    if (!activityHasPatient) {
      resetField("hidden");
    }
  }, [activityHasPatient, resetField]);

  // Reset invoicing codes when the category changes.
  useEffect(() => {
    if (currentCategory) {
      resetField("procedureCodes");
      resetField("productCodes");
    }
  }, [currentCategory, resetField]);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {isAdding ? (
          <Loading message={t`Lägger till aktiviteten`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : undefined}
            {/* ACTIVITY TYPE */}
            <Form.Section>
              <ActivityCategoryPicker
                categories={availableCategories}
                errorMessage={errors.category?.message}
                onChange={() => {
                  if (!currentCategoryRequiresCompetence) {
                    resetField(`requiredCompetences`);
                  }
                  if (!currentCategoryMayRequireDoubleStaffing) {
                    resetField(`doubleStaffing`);
                  }
                }}
              />
            </Form.Section>
            {/* MEASUREMENTS */}
            {currentCategory === categorySchema.enum.PatientMeasurementTask ? (
              <Form.Section>
                <MeasurementsPicker />
              </Form.Section>
            ) : null}
            {/* TITLE, DESCRIPTION */}
            <Form.Section width="half">
              <TitleInput />
              <DescriptionInput />
            </Form.Section>
            {/* SCHEDULING */}
            <Form.Section>
              <Scheduling />
            </Form.Section>
            {/* ACTIVITY DURATION */}
            {showDuration && currentCategoryAllowsDuration ? (
              <Form.Section>
                <ActivityDuration />
              </Form.Section>
            ) : null}
            {/* COMPETENCE */}
            {currentCategoryAllowsCompetence ? (
              <Form.Section>
                <CompetencePicker
                  isRequired={currentCategoryRequiresCompetence}
                />
              </Form.Section>
            ) : null}

            {/* INVOICING */}
            {activityMayHaveInvoicingCodes &&
            // `activityHasPatient` is already part of activityMayHaveInvoicingCodes, here for TypeScript to understand
            activityHasPatient ? (
              <Form.Section>
                <AddInvoicingCodesAction patientId={patientId} />
              </Form.Section>
            ) : null}

            {/* OTHER */}
            {currentCategoryMayRequireDoubleStaffing || activityIsHideable ? (
              <Form.Section>
                <Checkboxes legend={t`Övrigt`} orientation="horizontal">
                  {currentCategoryMayRequireDoubleStaffing ? (
                    <Checkbox
                      errorMessage={errors.doubleStaffing?.message}
                      label={{ text: t`Kräver dubbelbemanning` }}
                      visualStyle="framed"
                      {...register("doubleStaffing")}
                    />
                  ) : (
                    <></>
                  )}
                  {activityIsHideable ? (
                    <Checkbox
                      errorMessage={errors.hidden?.message}
                      label={{ text: t`Dölj aktiviteten för patienten` }}
                      visualStyle="framed"
                      {...register("hidden")}
                    />
                  ) : (
                    <></>
                  )}
                </Checkboxes>
              </Form.Section>
            ) : null}
            <FilledButton type="submit">
              <Trans>Skapa aktiviteten</Trans>
            </FilledButton>
          </>
        )}
      </Form>
    </FormProvider>
  );
};
